import { Button, StyledText } from 'UI';
import { ManualTypeEnum, PaymentDtoTypeEnum } from 'api/generated';
import { CaretIcon } from 'assets/icons';
import BonusBackground from 'assets/images/bonus-background.png';
import { BonusesModal } from 'components';
import { isCertificationCourse, isMvpPurchase } from 'components/Modals/BonusesModal/bonuses';
import { useBonusesContent } from 'components/Modals/BonusesModal/useBonusesContent';
import React, { FC, useLayoutEffect, useRef, useState } from 'react';
import { dayjs } from 'services';
import { selectors, useAppSelector } from 'store';
import styled, { css } from 'styled-components';
import { respondToWidth } from 'styles/general/respondTo';
import { trackAmplitudeEvent } from 'utils/trackAmplitudeEvent';

type Bonuses = {
  className?: string;
};

type BannerLayout = 'wide' | 'stacked' | 'compact';

// By the banner's own width, not the window's: beside the study plan it is much narrower than the
// screen. (styled-components 5 drops @container rules, so this is measured instead.)
const getLayout = (width: number): BannerLayout => {
  if (width >= 880) return 'wide';
  if (width >= 440) return 'stacked';
  return 'compact';
};

const useBannerLayout = (isEnabled: boolean) => {
  const ref = useRef<HTMLDivElement>(null);
  const [layout, setLayout] = useState<BannerLayout>('stacked');

  useLayoutEffect(() => {
    const element = ref.current;
    if (!isEnabled || !element) return;

    const update = () => setLayout(getLayout(element.getBoundingClientRect().width));
    update();

    if (typeof ResizeObserver === 'undefined') return;
    const observer = new ResizeObserver(update);
    observer.observe(element);
    return () => observer.disconnect();
  }, [isEnabled]);

  return { ref, layout };
};

const getOrdinal = (day: number) => {
  if (day % 10 === 1 && day % 100 !== 11) return 'st';
  if (day % 10 === 2 && day % 100 !== 12) return 'nd';
  if (day % 10 === 3 && day % 100 !== 13) return 'rd';
  return 'th';
};

const Bonuses: FC<Bonuses> = ({ className }) => {
  const [isShowUpgradeModal, setIsShowUpgradeModal] = useState(false);
  const paymentType = useAppSelector(selectors.courses.selectCurrentPaymentType);
  const activeCourse = useAppSelector(selectors.courses.selectActiveCourse);
  const isDarkMode = useAppSelector(selectors.settings.isDarkMode);
  const courseLevel = activeCourse?.currentPayment?.level;
  const notMvpSubscribe = courseLevel && (courseLevel === 'bronze' || courseLevel === 'silver');
  const isTrial = paymentType === PaymentDtoTypeEnum.Free;
  const isUpsell = !!(isTrial || notMvpSubscribe);
  // Certifications only: study materials keep the illustrated banner and their own bonus manual.
  const isIpta = isCertificationCourse(activeCourse);
  const { ref, layout } = useBannerLayout(isIpta);
  // Paid MVP members read the bonus-section-upgrade manual (claim links); everyone else, including
  // free trials, reads bonus-section-default — both edited in the admin.
  const isMember = isMvpPurchase(activeCourse?.currentPayment);
  const { bonusesContent, isReady } = useBonusesContent(isIpta ? activeCourse?.id : undefined, isMember);
  const today = dayjs();
  const day = today.date();

  const handleCheatSheetClick = (e: { preventDefault: () => void }) => {
    e.preventDefault();
    setIsShowUpgradeModal(true);

    if (isUpsell) {
      trackAmplitudeEvent('Get Bonuses Pressed');
    }
  };

  const bonusTextBeforePurchase = `Click to see MVP bonuses for ${today.format('MMMM')} ${day}${getOrdinal(day)}`;
  const bonusTextAfterPurchase = 'Click to see your MVP bonuses';
  const bonusText = isUpsell ? bonusTextBeforePurchase : bonusTextAfterPurchase;
  const buttonText = isUpsell ? 'Get bonuses' : 'See bonuses';

  const modal = (
    <BonusesModal
      referer="Get Bonuses"
      isOpen={isShowUpgradeModal}
      isMember={isMember}
      onClose={() => setIsShowUpgradeModal(false)}
      manualType={isUpsell ? ManualTypeEnum.BonusSectionDefault : ManualTypeEnum.BonusSectionUpgrade}
      content={isIpta ? bonusesContent : undefined}
    />
  );

  if (!isIpta) {
    return (
      <LegacyBanner className={className}>
        <LegacyText onClick={handleCheatSheetClick}>{bonusText}</LegacyText>

        <LinkContainer>
          <Link>
            <LegacyButton variant="tertiary" size="small" onClick={handleCheatSheetClick}>
              {buttonText}
            </LegacyButton>
          </Link>
        </LinkContainer>
        {modal}
      </LegacyBanner>
    );
  }

  return (
    <Banner ref={ref} className={className} $layout={layout}>
      <Intro>
        <Eyebrow onClick={handleCheatSheetClick}>{bonusText}</Eyebrow>
        <Title>{bonusesContent.title}</Title>
        <Subtitle>
          {isUpsell
            ? 'Unlock thousands of dollars in exclusive tools, training, and certifications — at no extra cost.'
            : 'Thousands of dollars in tools, training, and certifications — included with your MVP.'}
        </Subtitle>
        <GetButton variant="primary" size="small" onClick={handleCheatSheetClick}>
          {buttonText}
        </GetButton>
      </Intro>

      <Tiles $layout={layout} $isReady={isReady}>
        {bonusesContent.bannerTiles.map((bonus) => (
          <Tile
            key={bonus.id}
            type="button"
            onClick={handleCheatSheetClick}
            aria-label={`${bonus.shortName ?? bonus.name} — ${bonusText}`}>
            <TileLogo
              src={isDarkMode && bonus.logoDark ? bonus.logoDark : bonus.logo}
              alt=""
              $isTall={bonus.isTallLogo}
              $invert={isDarkMode && bonus.invertLogoInDark}
              loading="lazy"
            />
            <TileName>{bonus.shortName ?? bonus.name}</TileName>
            <TileFooter>
              <ValuePill>{bonus.shortValue ?? bonus.value}</ValuePill>
              {/* On phones the value needs the room; the whole tile is the button anyway. */}
              {layout !== 'compact' && <Chevron size={18} aria-hidden="true" />}
            </TileFooter>
          </Tile>
        ))}
      </Tiles>
      {modal}
    </Banner>
  );
};

export default Bonuses;

const Banner = styled.div<{ $layout: BannerLayout }>`
  width: 100%;
  display: grid;
  grid-template-columns: ${({ $layout }) =>
    $layout === 'wide' ? 'minmax(250px, 2fr) minmax(0, 5fr)' : 'minmax(0, 1fr)'};
  align-items: center;
  gap: ${({ $layout }) => ($layout === 'wide' ? '24px' : '20px')};
  padding: ${({ $layout }) => ($layout === 'compact' ? '16px' : '24px')};
  border: 1px solid ${({ theme: { colors } }) => colors.neutrals[8]};
  border-radius: 16px;
  background: ${({ theme: { colors } }) => colors.neutrals[11]};
  user-select: none;
`;

const Intro = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  min-width: 0;
`;

const Eyebrow = styled(StyledText)`
  ${({ theme: { typography } }) => typography.footnote_bold_12};
  color: ${({ theme: { colors } }) => colors.primary[1]};
  cursor: pointer;
`;

const Title = styled.h2`
  margin-top: 6px;
  ${({ theme: { typography } }) => typography.title_3_bold_28};
  color: ${({ theme: { colors } }) => colors.neutrals[1]};

  ${({ theme: { typography } }) => respondToWidth.s`
    ${typography.title_4_bold_24};
  `}
`;

const Subtitle = styled.p`
  margin-top: 8px;
  ${({ theme: { typography } }) => typography.body_basic_medium_14};
  color: ${({ theme: { colors } }) => colors.neutrals[2]};
`;

const GetButton = styled(Button)`
  width: auto;
  margin-top: 16px;
  white-space: nowrap;
  text-transform: capitalize;
`;

// Hidden until the manual has loaded, so edited tiles never flash the defaults first.
const Tiles = styled.div<{ $layout: BannerLayout; $isReady: boolean }>`
  min-width: 0;
  opacity: ${({ $isReady }) => ($isReady ? 1 : 0)};
  transition: opacity 200ms ease;
  display: grid;
  grid-template-columns: repeat(${({ $layout }) => ($layout === 'compact' ? 2 : 3)}, minmax(0, 1fr));
  gap: ${({ $layout }) => ($layout === 'compact' ? '10px' : '12px')};
`;

const Tile = styled.button`
  min-width: 0;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  padding: 14px;
  border: 1px solid ${({ theme: { colors } }) => colors.neutrals[8]};
  border-radius: 12px;
  background: ${({ theme: { colors } }) => colors.neutrals[10]};
  text-align: left;
  cursor: pointer;
  transition: transform 200ms ease, border-color 200ms ease, box-shadow 200ms ease;

  &:hover {
    transform: translateY(-2px);
    border-color: ${({ theme: { colors } }) => colors.primary[1]};
    box-shadow: 0 10px 24px rgba(20, 24, 38, 0.1);
  }

  &:focus-visible {
    outline: 2px solid ${({ theme: { colors } }) => colors.primary[1]};
    outline-offset: 2px;
  }

  @media (prefers-reduced-motion: reduce) {
    transition: none;

    &:hover {
      transform: none;
    }
  }

  ${respondToWidth.s`
    padding: 12px;
  `}
`;

const TileLogo = styled.img<{ $isTall?: boolean; $invert?: boolean }>`
  height: 26px;
  width: auto;
  max-width: 100%;
  object-fit: contain;
  object-position: left center;

  ${({ $isTall }) =>
    $isTall &&
    css`
      height: 40px;
      margin: -7px 0;
    `}

  ${({ $invert }) =>
    $invert &&
    css`
      filter: brightness(0) invert(1);
    `}
`;

const TileName = styled.span`
  margin-top: 8px;
  ${({ theme: { typography } }) => typography.body_basic_bold_14};
  line-height: 1.3;
  color: ${({ theme: { colors } }) => colors.neutrals[1]};
`;

const TileFooter = styled.span`
  width: 100%;
  margin-top: auto;
  padding-top: 10px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
`;

// Neutral on purpose: Get Bonuses is the banner's only accent-coloured button.
const ValuePill = styled.span`
  min-width: 0;
  padding: 3px 8px;
  border-radius: 6px;
  ${({ theme: { typography } }) => typography.footnote_bold_12};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  color: ${({ theme: { colors } }) => colors.neutrals[1]};
  background: ${({ theme: { colors } }) => colors.neutrals[11]};
`;

const Chevron = styled(CaretIcon)`
  flex-shrink: 0;
`;

/* Study materials: the original illustrated banner, unchanged. The artwork keeps its own
   proportions (799x246) at any width; the box still grows if the content needs more room. */
const LegacyBanner = styled.div`
  width: 100%;
  aspect-ratio: 799 / 246;
  display: flex;
  flex-direction: column;
  flex-wrap: wrap;
  user-select: none;
  background-image: url(${BonusBackground});
  background-size: cover;
  background-repeat: no-repeat;
  background-position: center;
`;

/**
 * Content sizes itself and wraps naturally inside the aspect-ratio banner. The old fixed 350px
 * width and per-width font ladders included 5px/10px line-heights that made wrapped lines paint
 * over each other on phones.
 */
const LegacyText = styled(StyledText)`
  width: auto;
  max-width: calc(100% - 38px);
  ${({ theme: { typography } }) => typography.body_large_bold_16}
  color: ${({ theme: { colors } }) => colors.primary[1]};
  cursor: pointer;
  margin-top: 20px;
  margin-left: 19px;

  ${respondToWidth.s`
    margin-top: 12px;
    font-size: 14px;
    line-height: 20px;
  `}
`;

const LegacyButton = styled(Button)`
  width: fit-content;
  padding: 8px 32px;
  opacity: 1 !important;
  background: ${({ theme: { colors } }) => colors.primary[1]} !important;
  color: #ffffff !important;

  white-space: nowrap;
  text-transform: capitalize;

  ${respondToWidth.s`
    font-size: 14px;
    padding: 7px 24px;
  `}
`;

const LinkContainer = styled.div`
  width: fit-content;
  max-width: calc(100% - 19px);
  margin-top: 25px;
  margin-left: 19px;

  ${respondToWidth.s`
    margin-top: 12px;
  `}
`;

const Link = styled.div`
  width: 100%;
  ${respondToWidth.sm`
    max-width: 100%;
  `}
`;
